/**
 * 共有テキストマスタ (`GET /synced-texts`) の一覧を配る軽量コンテキスト。
 *
 * 放送地点マスタ > コンテンツ編集画面の任意テキスト放送「共有テキスト」で、参照先の選択と
 * 本文の表示 (無効化したテキストエリア) に使う。
 *
 * `FreeTextTemplateContext` と同じ方式 (中間層に props を通すだけの増加を避ける)。
 * Provider が無い環境でも動くよう、デフォルト値は空配列とする。
 *
 * ⚠️ ここで配る本文は **表示専用**。放送地点には保存されない (参照 `synced_ref` のみ保存)。
 */
import { createContext, useContext, useMemo } from 'react';

import type { SyncedText } from '../api/types';

export interface SyncedTextContextValue {
  /** 登録済みの共有テキスト (未取得 / 取得失敗は空配列) */
  texts: readonly SyncedText[];
}

const EMPTY_TEXTS: readonly SyncedText[] = [];

const SyncedTextContext = createContext<SyncedTextContextValue>({ texts: EMPTY_TEXTS });

export function SyncedTextProvider({
  texts,
  children,
}: {
  texts: readonly SyncedText[];
  children: React.ReactNode;
}): React.JSX.Element {
  const value = useMemo<SyncedTextContextValue>(() => ({ texts }), [texts]);
  return <SyncedTextContext.Provider value={value}>{children}</SyncedTextContext.Provider>;
}

/** 入力欄から、登録済みの共有テキスト一覧を取得する。 */
export function useSyncedTexts(): readonly SyncedText[] {
  return useContext(SyncedTextContext).texts;
}
